import { useEffect, useMemo, useState } from "react";
import type { NewsItem } from "@/types/news";
import { sortByTimeAsc, sortByTimeDesc } from "@/utils/news";
import NewsCard from "@/components/feature/NewsCard";
import Pagination from "@/components/feature/Pagination";

interface NewsBrowserProps {
  items: NewsItem[];
  emptyText?: string;
  pageSize?: number;
  initialSort?: "desc" | "asc";
  initialExclusiveOnly?: boolean;
}

export default function NewsBrowser({
  items,
  emptyText = "暂无相关内容，敬请期待。",
  pageSize = 5,
  initialSort = "desc",
  initialExclusiveOnly = false,
}: NewsBrowserProps) {
  const [sort, setSort] = useState<"desc" | "asc">(initialSort);
  const [exclusiveOnly, setExclusiveOnly] = useState(initialExclusiveOnly);
  const [page, setPage] = useState(1);

  const filtered = useMemo(() => {
    const base = exclusiveOnly
      ? items.filter((item) => item.exclusive)
      : items;
    return sort === "desc" ? sortByTimeDesc(base) : sortByTimeAsc(base);
  }, [items, sort, exclusiveOnly]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));

  useEffect(() => {
    setPage(1);
  }, [sort, exclusiveOnly, items]);

  useEffect(() => {
    if (page > totalPages) {
      setPage(totalPages);
    }
  }, [page, totalPages]);

  const pageItems = filtered.slice((page - 1) * pageSize, page * pageSize);

  return (
    <div>
      <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-sm text-foreground-600">
          共 <strong className="text-accent-400">{filtered.length}</strong> 条爆料
        </p>
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2 rounded-md border border-background-300 bg-background-100 px-2 py-1">
            <span className="flex h-4 w-4 items-center justify-center text-sm text-foreground-500">
              <i className="ri-sort-desc" />
            </span>
            <select
              value={sort}
              onChange={(event) => setSort(event.target.value as "desc" | "asc")}
              aria-label="排序方式"
              className="cursor-pointer bg-transparent py-1 text-sm text-foreground-800 focus:outline-none"
            >
              <option value="desc" className="bg-background-200">
                最新优先
              </option>
              <option value="asc" className="bg-background-200">
                最早优先
              </option>
            </select>
          </div>

          <button
            type="button"
            onClick={() => setExclusiveOnly((prev) => !prev)}
            aria-pressed={exclusiveOnly}
            className={`flex items-center gap-2 rounded-md border px-3 py-1.5 text-sm transition-colors ${
              exclusiveOnly
                ? "border-accent-500 bg-accent-500 font-medium text-background-50"
                : "border-background-300 bg-background-100 text-foreground-700 hover:border-accent-500"
            }`}
          >
            <span className="flex h-4 w-4 items-center justify-center">
              <i className="ri-flashlight-line" />
            </span>
            仅看独家
          </button>
        </div>
      </div>

      {pageItems.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-100 py-16 text-center">
          <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-200 text-2xl text-foreground-500">
            <i className="ri-inbox-line" />
          </span>
          <p className="mt-4 text-sm text-foreground-600">{emptyText}</p>
        </div>
      ) : (
        <div className="space-y-4">
          {pageItems.map((item) => (
            <NewsCard key={item.id} item={item} />
          ))}
        </div>
      )}

      <div className="mt-8">
        <Pagination
          currentPage={page}
          totalPages={totalPages}
          onChange={setPage}
        />
      </div>
    </div>
  );
}